import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { UserData } from "../../../Context/DataContext";
import { LanguageContext } from "../../../Context/changeLanguageProvider";
import styles from "./styles/PartnerFileShare.module.css";
import FileTypeIcon, { FolderIcon } from "./FileTypeIcon";
import { RandomId } from "../../../Utils/RandomId";
import { DeleteFile, UploadFiles, downloadFile } from "../../../Utils/UploadFile";
import { floatMessage } from "../../../Utils/floatMessage";
import {
  FOLDER_TYPE,
  MAX_FILES,
  STORAGE_CAPACITY,
  buildTree,
  filesFromDrop,
  folderPathOf,
  readableSize,
  saveSharedFiles,
  screenUpload,
  typeLabel,
} from "../../../Utils/partnerFiles";

/*
  Files the customer hands to their partner.

  A small file manager: what has been shared, how much room is left, and a
  way to add more. The customer uploads and removes; the partner opens the
  same panel from their own copy of the promotion card and can only read
  and download. That asymmetry is the feature -- it is a drop box pointing
  one way, so that documents need not go by email.

  Everything is encrypted in the browser before it leaves, under the
  customer's own key, exactly as an asset's documents are. The server
  stores bytes it cannot read and a list of ids that describe nothing; the
  names, sizes and folder paths are encrypted with everything else. The
  partner can open them because they already hold a wrap of the customer's
  key -- the same one that lets them read a shared asset.
*/

const FALLBACK = {
  partnerFilesTitle: "Share files securely with {name}",
  partnerFilesTitleShared: "Files your customer has shared with you",
  partnerFilesName: "Name",
  partnerFilesModified: "Date modified",
  partnerFilesType: "Type",
  partnerFilesSize: "Size",
  partnerFilesStatus: "Status",
  partnerFilesEmpty: "No files shared yet.",
  partnerFilesCount: "{used} of {max} files uploaded",
  partnerFilesStorage:
    "You are currently using {used} of your {total} storage capacity.",
  partnerFilesMax: "Max file size: 100MB.",
  partnerFilesUpload: "Upload New Files",
  partnerFilesDrop: "Drop files here to share them",
  partnerFilesNote:
    "{name} can view and download files from this screen. This lets you securely share files with {name} without relying on email, while keeping better control over your files and access permissions.",
  partnerFilesDeleteNote:
    "If you delete a file here, {name} will no longer be able to access it through OSTREON, unless they have already saved a local copy.",
  partnerFilesBlocked: "{name} was not uploaded: programs cannot be shared.",
  partnerFilesTooLarge: "{name} was not uploaded: over the 100MB file limit.",
  partnerFilesBlockedMany:
    "{count} files were not uploaded: programs cannot be shared.",
  partnerFilesTooLargeMany:
    "{count} files were not uploaded: each is over the 100MB limit.",
  partnerFilesBatchTooMany:
    "Nothing was uploaded: that would be {total} files and the limit is {max}. You can add {room} more.",
  partnerFilesBatchNoStorage:
    "Nothing was uploaded: that needs {needed} and only {free} is left.",
  partnerFilesFailed: "{name} could not be uploaded.",
  partnerFilesRemoveFailed: "That file could not be removed. Try again.",
  partnerFilesNotRecorded:
    "{name} was uploaded but could not be saved to the list. Try again.",
  partnerFilesDelete: "Remove",
  partnerFilesDownload: "Download",
  partnerFilesGone:
    "{name} could not be downloaded. The file is no longer in storage -- remove this row and upload it again.",
  partnerFilesDownloadFailed: "{name} could not be downloaded.",
};

/*
  Only the per-file refusals. Running out of room is reported once for the
  whole batch, with the numbers -- see `refused` in accept().

  Two wordings each: one naming the file, and one counting them. A picked
  FOLDER is routinely hundreds of files, and a real one holds scripts --
  which are refused -- so naming each would have put a few hundred toasts
  on screen and buried everything else, the count message included.
*/
const REASON_KEY = {
  blocked: "partnerFilesBlocked",
  too_large: "partnerFilesTooLarge",
};
const REASON_MANY_KEY = {
  blocked: "partnerFilesBlockedMany",
  too_large: "partnerFilesTooLargeMany",
};

/* A date as a file manager writes it: 7/20/2021 11:57 AM. */
const readableDate = (value, french) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return "";
  const date = new Date(n);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString(french ? "fr-FR" : "en-US", {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

export default function PartnerFileShare({ partnerName }) {
  const userData = UserData();
  const websiteCurrentLang = useContext(LanguageContext).language;
  const [content, setContent] = useState(
    websiteCurrentLang?.content?.DublicatedText || {}
  );
  useEffect(() => {
    setContent(websiteCurrentLang?.content?.DublicatedText || {});
  }, [websiteCurrentLang]);
  const t = (key) => content?.[key] || FALLBACK[key];
  const french = websiteCurrentLang?.label === "fr";

  /*
    A partner reading their customer's copy. They may open a file and
    nothing else: no upload control, no delete handle, and the writers
    behind both are stubbed in that view anyway.
  */
  const readOnly = Boolean(userData.partnerReadOnly);
  const customerId = readOnly ? userData.UserData?.user_id : null;

  const files = useMemo(
    () =>
      Array.isArray(userData.UserData?.partner_shared_files)
        ? userData.UserData.partner_shared_files
        : [],
    [userData.UserData?.partner_shared_files]
  );

  /*
    Rows still in flight, and ones that failed.

    Held apart from the saved list rather than written into it: a file is
    not shared until its bytes are stored, and a row that is still
    uploading must not become something the partner can be told to fetch.
  */
  const [pending, setPending] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [open, setOpen] = useState(() => new Set());
  const inputRef = useRef(null);

  /*
    ---- carrying the wheel on past the end of the list ----

    A browser latches a wheel gesture to whichever scroller it began on, so
    once the list reaches its last row the wheel keeps being delivered there
    and the modal behind it stays still. The reader has to stop, move the
    pointer off the list and start again. `overscroll-behavior` is no help:
    it can stop chaining, not start it.

    Both directions, because both are latched. Two things stop the handover
    from stuttering, and the stutter was only ever visible going DOWN:

    - A 2px tolerance on the edge test. The top edge is exact
      (`scrollTop <= 0`), but the bottom is a sum of three values that are
      fractional at most zoom levels and pixel ratios -- so it flickered
      between "at the end" and "not quite", and the wheel alternated
      between moving the modal and nudging the list. That alternation is
      what read as jumping.

    - A short latch of our own. Once this has taken a gesture over it keeps
      it for 150ms in the SAME direction, rather than re-deciding on every
      event while the list slides out from under the pointer. Reversing
      direction drops the latch immediately, so scrolling back into the
      list still works.

    The event is released when the modal itself has nothing left that way,
    so the page behind can still be scrolled from over the list.

    Attached natively rather than through onWheel: React's wheel listener is
    passive, so preventDefault inside it is ignored with a console warning,
    and without preventDefault two things scroll at once.
  */
  const listRef = useRef(null);
  const wheelLatch = useRef({ at: 0, direction: 0 });
  useEffect(() => {
    const list = listRef.current;
    if (!list) return undefined;

    // The nearest ancestor that actually scrolls -- whichever modal hosts us.
    const outerScroller = () => {
      let node = list.parentElement;
      while (node && node !== document.body) {
        const style = window.getComputedStyle(node);
        const scrolls = /(auto|scroll)/.test(style.overflowY);
        if (scrolls && node.scrollHeight > node.clientHeight + 1) return node;
        node = node.parentElement;
      }
      return null;
    };

    const EDGE = 2;
    const LATCH_MS = 150;

    const onWheel = (event) => {
      if (!event.deltaY) return;
      const direction = event.deltaY > 0 ? 1 : -1;
      const atTop = list.scrollTop <= EDGE;
      const atBottom =
        list.scrollTop + list.clientHeight >= list.scrollHeight - EDGE;
      const past = direction > 0 ? atBottom : atTop;
      const latched =
        direction === wheelLatch.current.direction &&
        event.timeStamp - wheelLatch.current.at < LATCH_MS;
      if (!past && !latched) return;

      const outer = outerScroller();
      if (!outer) return;
      const outerDone =
        direction > 0
          ? outer.scrollTop + outer.clientHeight >= outer.scrollHeight - 1
          : outer.scrollTop <= 0;
      if (outerDone) return;

      outer.scrollTop += event.deltaY;
      wheelLatch.current = { at: event.timeStamp, direction };
      event.preventDefault();
    };

    list.addEventListener("wheel", onWheel, { passive: false });
    return () => list.removeEventListener("wheel", onWheel);
  }, []);
  // Highlighted while something is being dragged over the button itself.
  const [droppingOnButton, setDroppingOnButton] = useState(false);
  /*
    Why the last pick was refused, kept on screen.

    A toast was not enough here. Choosing a folder puts Chrome's own
    "Upload N files to this site?" in front of the customer, and after
    they accept it the browser spends a long time handing over tens of
    thousands of file handles -- by which point a message that shows for
    six seconds and disappears is easy to miss entirely, and the panel
    looks as though it simply ignored them. This stays until the next
    pick answers it.
  */
  const [problem, setProblem] = useState(null);

  const usedStorage = Number(userData.CustomerUser?.used_storage) || 0;
  const tree = useMemo(() => buildTree(files), [files]);

  const name = String(partnerName || "").trim();
  const withName = (key) => t(key).split("{name}").join(name);

  /*
    Somewhere a drop is accepted.

    The same handlers on the table and on the button, because a customer
    dragging a folder aims at whichever of the two reads as "put it
    here", and the button says so more plainly than a list does.

    Drag and drop is also the ONLY way a folder gets in now: the file
    picker cannot offer both, and the folder picker brought Chrome's
    "Upload N files to this site?" with it. A drop has no such prompt and
    reads the directory itself (see filesFromDrop).
  */
  const dropZone = (setActive) => ({
    onDragOver: (e) => {
      if (readOnly) return;
      e.preventDefault();
      setActive(true);
    },
    onDragLeave: (e) => {
      // Ignore the crossings between a target's own children.
      if (e.currentTarget.contains(e.relatedTarget)) return;
      setActive(false);
    },
    onDrop: async (e) => {
      if (readOnly) return;
      e.preventDefault();
      setActive(false);
      /*
        Read through the entry API, not dataTransfer.files: the latter
        hands back a dropped FOLDER as one zero-byte entry and none of
        its contents.
      */
      accept(await filesFromDrop(e.dataTransfer));
    },
  });

  const toggle = (path) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });

  /* ---------------------------------------------------------------- save */
  /*
    Stored through its own endpoint, and only then shown as saved.

    Not through setUserData/Update: that posts the whole document as this
    screen sees it, and any other screen doing the same with a snapshot
    taken before the upload finished erased the list -- the file was
    already on disk, so the orphan sweeper deleted the bytes too. The
    local copy is updated with the functional setter for the same reason,
    so it merges into whatever the context holds now rather than
    overwriting it with a stale object.
  */
  const persist = async (nextFiles) => {
    await saveSharedFiles(nextFiles, userData.private_key);
    userData.setUserData((prev) => ({
      ...prev,
      partner_shared_files: nextFiles,
    }));
  };

  /* -------------------------------------------------------------- upload */
  const accept = async (chosen) => {
    if (readOnly || userData.isFreezed) return;
    const list = Array.from(chosen || []);
    if (!list.length) return;

    const { accepted, rejected, refused } = screenUpload(list, {
      existing: files.length + pending.length,
      used: usedStorage,
    });
    // Whatever the last pick said, this one answers.
    setProblem(null);
    /*
      The per-file refusals: a program, or something over the 100MB limit.

      One toast per REASON, already tallied by screenUpload. A single file
      is named, because removing it is the whole of the fix; more than one
      is counted, because a picked folder is routinely tens of thousands
      and naming each buried every other message on the screen.
    */
    const said = [];
    for (const bad of rejected) {
      said.push(
        bad.count === 1
          ? t(REASON_KEY[bad.reason] || "partnerFilesFailed")
              .split("{name}")
              .join(bad.name)
          : t(REASON_MANY_KEY[bad.reason] || "partnerFilesFailed")
              .split("{count}")
              .join(String(bad.count))
      );
    }
    /*
      And the one that stops everything: no room. Counted before a byte is
      sent, so nothing is uploaded and then abandoned, and said with the
      numbers -- "that would be 104 files" is actionable, sixty arbitrary
      files appearing is not.
    */
    if (refused) {
      said.push(
        refused.reason === "too_many"
          ? t("partnerFilesBatchTooMany")
              .split("{room}")
              .join(String(refused.room))
              .split("{total}")
              .join(String(refused.total))
              .split("{existing}")
              .join(String(refused.existing))
              .split("{max}")
              .join(String(refused.max))
          : t("partnerFilesBatchNoStorage")
              .split("{needed}")
              .join(readableSize(refused.needed))
              .split("{free}")
              .join(readableSize(refused.free))
      );
    }

    /*
      Said twice, on purpose: once as a toast, and once in the panel where
      it stays. See `problem` above -- after Chrome's folder prompt the
      toast is too easily missed.
    */
    if (said.length) {
      setProblem(said);
      for (const line of said) floatMessage(line, "error");
    }
    if (refused) return;
    if (!accepted.length) return;

    /*
      One at a time, so a long batch reports as it goes and one failure
      costs only its own file. The id is the stored filename, as it is
      everywhere else in this product.
    */
    const marks = accepted.map((file) => ({
      key: RandomId(10),
      name: file.name,
      size: file.size,
      lastModified: file.lastModified,
      path: folderPathOf(file),
      status: "uploading",
    }));
    setPending((prev) => [...prev, ...marks]);

    /*
      Saved one at a time, not once at the end.

      A batch can be sixty files and several minutes; a failure, a closed
      tab or a reload partway through used to lose every file already
      uploaded, because nothing was recorded until the last one finished.
      Worse, the bytes were already in storage with nothing referencing
      them -- which is precisely what the orphan sweeper deletes.

      `kept` is the running list rather than re-reading `files`, because
      the save is asynchronous: the context has not caught up by the time
      the next file finishes.
    */
    let kept = files;
    let anyStored = false;
    for (let i = 0; i < accepted.length; i += 1) {
      const file = accepted[i];
      const mark = marks[i];
      const id = RandomId(32);
      let uploaded = false;
      try {
        // Renamed to its id before encryption, which is what names it in storage.
        // eslint-disable-next-line no-await-in-loop
        await UploadFiles(
          [new File([file], id, { type: file.type })],
          userData.private_key,
          () => {},
          content
        );
        uploaded = true;
        kept = [
          ...kept,
          {
            id,
            name: file.name,
            size: String(file.size),
            lastModified: String(file.lastModified || Date.now()),
            path: mark.path,
            mime: file.type || "",
          },
        ];
        anyStored = true;
        // Awaited: the row is only shown as shared once the list is stored.
        // eslint-disable-next-line no-await-in-loop
        await persist(kept);
        // Its row moves from the pending list into the saved one.
        setPending((prev) => prev.filter((p) => p.key !== mark.key));
      } catch (error) {
        /*
          Two different failures, said differently. The bytes landing and
          the list recording them are separate steps, and reporting a
          failed SAVE as a failed upload sent the last round of debugging
          at the wrong half of it.
        */
        console.log(error);
        setPending((prev) =>
          prev.map((p) => (p.key === mark.key ? { ...p, status: "failed" } : p))
        );
        floatMessage(
          t(uploaded ? "partnerFilesNotRecorded" : "partnerFilesFailed")
            .split("{name}")
            .join(file.name),
          "error"
        );
      }
    }

    if (anyStored) {
      /*
        `used_storage` lives on CustomerUser and is recomputed server-side
        as each file lands, so the figure under the table is stale the
        moment an upload finishes. Asked for again rather than guessed at
        locally: the server counts the whole folder, which is the number
        the Subscription tab shows too.
      */
      userData.getCustomerUser();
    }
  };

  /* -------------------------------------------------------------- remove */
  const remove = async (row) => {
    if (readOnly || userData.isFreezed) return;
    const doomed =
      row.kind === "folder"
        ? files.filter(
            (f) => f.path === row.path || String(f.path || "").startsWith(`${row.path}/`)
          )
        : [row];
    try {
      /*
        The record is what the partner reads, so it goes first. The bytes
        follow -- and the endpoint sweeps after storing the list anyway,
        so a file dropped from it is collected even if this call fails.
      */
      await persist(files.filter((f) => !doomed.some((d) => d.id === f.id)));
    } catch (error) {
      console.log(error);
      floatMessage(t("partnerFilesRemoveFailed"), "error");
      return;
    }
    /*
      Awaited, then the figure is asked for again.

      Removing a file changes the account's storage, and the line under
      the table reads it off CustomerUser -- which only changes when it is
      fetched. Without this the usage stayed where it was until something
      else happened to reload it, and removing a 50MB file looked like it
      had freed nothing.
    */
    await Promise.all(
      doomed.map((file) => DeleteFile(file.id).catch((e) => console.log(e)))
    );
    userData.getCustomerUser();
  };

  /* ------------------------------------------------------------ download */
  const openFile = (row) => {
    if (row.kind !== "file") return;
    downloadFile(
      row.id,
      row.name,
      userData.private_key,
      () => {},
      content,
      customerId
    ).catch((error) => {
      /*
        A failed download used to go to the console and nowhere else, so the
        row simply did nothing when it was clicked -- which is how a file
        whose bytes are missing came to be reported as "not downloadable"
        rather than as an error, and why it took a reproduction to find.

        500 is that case specifically: both download endpoints stream the
        file straight off disk, so it is what they answer when there is
        nothing to stream. The row can only be removed and re-uploaded --
        the metadata lives in `partner_shared_files` and the bytes do not,
        so one can outlive the other. Anything else is reported plainly
        rather than guessed at.
      */
      console.log(error);
      const gone = String(error?.message || "") === "500";
      floatMessage(
        t(gone ? "partnerFilesGone" : "partnerFilesDownloadFailed")
          .split("{name}")
          .join(row.name),
        "error"
      );
    });
  };

  /* ---------------------------------------------------------------- rows */
  const renderRow = (row, depth) => {
    const isFolder = row.kind === "folder";
    const expanded = isFolder && open.has(row.path);
    return (
      <React.Fragment key={isFolder ? `d:${row.path}` : `f:${row.id}`}>
        <tr
          className={styles.row}
          onClick={() => (isFolder ? toggle(row.path) : openFile(row))}
          title={isFolder ? row.name : t("partnerFilesDownload")}
        >
          <td className={styles.cell_remove}>
            {!readOnly && (
              <button
                type="button"
                className={styles.remove}
                aria-label={`${t("partnerFilesDelete")} ${row.name}`}
                title={`${t("partnerFilesDelete")} ${row.name}`}
                onClick={(e) => {
                  e.stopPropagation();
                  remove(row);
                }}
              />
            )}
          </td>
          <td className={styles.cell_name}>
            <span style={{ paddingLeft: `${depth * 18}px` }} className={styles.name}>
              {/*
                Only a folder gets the arrow. A file used to reserve the
                same width to keep the two aligned, which indented every
                name by 10px for the sake of an alignment nobody asked
                for -- the nesting is already shown by the indent below.
              */}
              {isFolder && (
                <span
                  className={`${styles.twisty} ${expanded ? styles.twisty_open : ""}`}
                  aria-hidden="true"
                />
              )}
              {isFolder ? (
                <FolderIcon className={styles.icon} />
              ) : (
                <FileTypeIcon className={styles.icon} name={row.name} />
              )}
              <span className={styles.name_text}>{row.name}</span>
            </span>
          </td>
          <td className={styles.cell_modified}>
            {readableDate(row.lastModified, french)}
          </td>
          <td className={styles.cell_type}>
            {isFolder ? FOLDER_TYPE : typeLabel(row.name)}
          </td>
          <td className={styles.cell_size}>
            {isFolder ? "" : readableSize(row.size)}
          </td>
          <td className={styles.cell_status}>
            <span className={styles.status_done} aria-label="shared" />
          </td>
          {/*
            The row already downloads when clicked, and nothing said so --
            the only hint was a tooltip, which has to be waited for and
            never appears on a touch screen. This is that same action, drawn.

            Files only: a folder row expands, it does not download. And only
            on a row that is already stored -- a file still uploading has no
            id to fetch yet, which is why the pending rows below leave this
            cell empty rather than offering a button that cannot work.
          */}
          <td className={styles.cell_download}>
            {!isFolder && (
              <button
                type="button"
                className={styles.download}
                aria-label={`${t("partnerFilesDownload")} ${row.name}`}
                title={`${t("partnerFilesDownload")} ${row.name}`}
                onClick={(e) => {
                  // The row would otherwise download it a second time.
                  e.stopPropagation();
                  openFile(row);
                }}
              />
            )}
          </td>
        </tr>
        {expanded && row.children.map((child) => renderRow(child, depth + 1))}
      </React.Fragment>
    );
  };

  const total = files.length + pending.filter((p) => p.status !== "done").length;

  return (
    <div className={styles.section}>
      {/*
        Whose screen this is decides the sentence. "Share files securely
        with X" is an instruction to the customer; the partner is the one
        being shared WITH, and reading their own name there would be
        nonsense.
      */}
      <div className={styles.title}>
        {readOnly ? t("partnerFilesTitleShared") : withName("partnerFilesTitle")}
      </div>

      <div
        ref={listRef}
        className={`${styles.table_wrap} ${dragging ? styles.table_dragging : ""}`}
        {...dropZone(setDragging)}
      >
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.cell_remove} />
              <th className={styles.cell_name}>{t("partnerFilesName")}</th>
              <th className={styles.cell_modified}>{t("partnerFilesModified")}</th>
              <th className={styles.cell_type}>{t("partnerFilesType")}</th>
              <th className={styles.cell_size}>{t("partnerFilesSize")}</th>
              <th className={styles.cell_status}>{t("partnerFilesStatus")}</th>
              {/* The download control's own column; the heading is the icon. */}
              <th className={styles.cell_download} />
            </tr>
          </thead>
          <tbody>
            {tree.map((row) => renderRow(row, 0))}
            {pending.map((p) => (
              <tr className={styles.row} key={p.key}>
                <td className={styles.cell_remove} />
                <td className={styles.cell_name}>
                  <span className={styles.name}>
                      <FileTypeIcon className={styles.icon} name={p.name} />
                    <span className={styles.name_text}>{p.name}</span>
                  </span>
                </td>
                <td className={styles.cell_modified}>
                  {readableDate(p.lastModified, french)}
                </td>
                <td className={styles.cell_type}>{typeLabel(p.name)}</td>
                <td className={styles.cell_size}>{readableSize(p.size)}</td>
                <td className={styles.cell_status}>
                  {p.status === "failed" ? (
                    <span className={styles.status_failed} aria-label="failed" />
                  ) : (
                    <span className={styles.status_working} aria-label="uploading" />
                  )}
                </td>
                <td className={styles.cell_download} />
              </tr>
            ))}
            {!tree.length && !pending.length && (
              <tr>
                <td className={styles.empty} colSpan={7}>
                  {t("partnerFilesEmpty")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {dragging && <div className={styles.drop_hint}>{t("partnerFilesDrop")}</div>}
      </div>

      <div className={styles.counts}>
        <div>
          {t("partnerFilesCount")
            .split("{used}")
            .join(String(total))
            .split("{max}")
            .join(String(MAX_FILES))}
        </div>
        <div>
          {t("partnerFilesStorage")
            .split("{used}")
            .join(readableSize(usedStorage))
            .split("{total}")
            .join(readableSize(STORAGE_CAPACITY))}
        </div>
        <div>{t("partnerFilesMax")}</div>
      </div>

      {/*
        The last refusal, left on screen until the next pick answers it.
        A toast alone was missed: the folder prompt and the browser's own
        work push it well past the point the customer is still watching.
      */}
      {problem && (
        <div className={styles.problem} role="alert">
          {problem.map((line) => (
            <div key={line}>{line}</div>
          ))}
        </div>
      )}

      {!readOnly && (
        <>
          {/*
            Files only. `webkitdirectory` turns a picker into a FOLDER
            picker rather than adding folders to it, so no panel takes
            both -- and the folder picker brought Chrome's "Upload N
            files to this site?" with it, and handed back the whole tree
            before the page could refuse it.

            A folder comes in by being dropped instead, on the table or
            on the button. A drop has no such prompt and is read one
            entry at a time (see filesFromDrop).
          */}
          <input
            ref={inputRef}
            type="file"
            multiple
            hidden
            onChange={(e) => {
              accept(e.target.files);
              e.target.value = "";
            }}
          />
          <div className={styles.actions}>
            <button
              type="button"
              className={`${styles.upload} ${
                droppingOnButton ? styles.upload_dropping : ""
              }`}
              disabled={userData.isFreezed}
              onClick={() => inputRef.current?.click()}
              {...dropZone(setDroppingOnButton)}
            >
              {t("partnerFilesUpload")}
            </button>
          </div>
        </>
      )}

      <div className={styles.note}>
        <p>{withName("partnerFilesNote")}</p>
        {!readOnly && <p>{withName("partnerFilesDeleteNote")}</p>}
      </div>
    </div>
  );
}
